import type { GoodsData } from '@banhala/ui-kit';

/**
 * render.data 내부의 공통 구조.
 * 각 API 응답 타입의 render.data와 호환됩니다.
 */
export interface RenderableGoodsRenderData {
  image: {
    url: string;
    use_animation: boolean;
    ratio: number;
  };
  name: string;
  price: string;
  has_ad_badge: boolean;
  name_lines: number;
  discount_rate: string | null;
  closed_reason: string | null;
  image_badge_list: Array<{
    type: string;
    image_url: string;
    image_width: number;
    image_height: number;
  }>;
  chips:
    | Array<{
        name: string;
        type: string;
        meta?: {
          bg_color: string;
          border_color: string;
          border_width?: number;
          rounded_corner_radius?: number;
          text_color: string;
          text_style: string;
        };
      }>
    | null;
  emblems?: { image_url: string }[];
  store_text: string | null;
  nudging_text: string | null;
  top_nudging_text?: string | null;
  price_description?: {
    text: string;
  } | null;
  actions?: string[];
}

/**
 * 공통 변환에 필요한 최소 입력 구조.
 */
export interface RenderableGoodsItem {
  item: {
    sno: number;
    delivery_type: string;
  };
  render: {
    data: RenderableGoodsRenderData;
  };
}

export type TopNudgingStrategy = 'conditional-spread' | 'separate-fields';

export interface ConvertOptions {
  /** thumbnail.src 직접 지정. 기본값: render.data.image.url */
  thumbnailSrc?: string;

  /**
   * topNudging/priceDescription 처리 전략.
   * - 'conditional-spread': price 객체 내부에 조건부 spread (goods/review 컨버터 패턴)
   * - 'separate-fields': contents 레벨에 별도 필드 (market/shared 컨버터 패턴)
   * 기본값: 'conditional-spread'
   */
  topNudgingStrategy?: TopNudgingStrategy;

  /** thumbnail에 추가할 필드 (hideLike, isSoldOut 등) */
  thumbnailOverrides?: Record<string, unknown>;

  /** contents에 추가할 필드 */
  contentsOverrides?: Record<string, unknown>;

  /** name 필드 오버라이드 */
  nameOverrides?: Partial<GoodsData['contents']['name']>;

  /** price 필드 오버라이드 */
  priceOverrides?: Record<string, unknown>;
}

/**
 * API 응답의 render.data를 GoodsData로 변환하는 공통 유틸리티.
 *
 * 모든 GoodsData 컨버터에서 공유하는 로직을 통합합니다.
 * 컨버터별 차이점은 options로 주입합니다.
 */
export const convertRenderDataToGoodsData = (
  input: RenderableGoodsItem,
  options: ConvertOptions = {},
): GoodsData => {
  const { render, item } = input;
  const { data } = render;
  const {
    thumbnailSrc = data.image.url,
    topNudgingStrategy = 'conditional-spread',
    thumbnailOverrides,
    contentsOverrides,
    nameOverrides,
    priceOverrides,
  } = options;

  const badges = data.image_badge_list.map(badge => ({
    uri: badge.image_url,
    width: badge.image_width,
    height: badge.image_height,
  }));

  const chips = (data.chips ?? []).map(chip => {
    if (chip.meta) {
      return {
        type: chip.type,
        name: chip.name,
        customTag: {
          bgColor: chip.meta.bg_color,
          borderColor: chip.meta.border_color,
          borderWidth: chip.meta.border_width ?? 0,
          borderRadius: chip.meta.rounded_corner_radius ?? 0,
          textColor: chip.meta.text_color,
          textType: chip.meta.text_style,
        },
      };
    }
    return {
      type: chip.type,
      name: chip.name,
    };
  });

  const basePrice: Record<string, unknown> = {
    price: data.price,
    discount: data.discount_rate ?? undefined,
    ...priceOverrides,
  };

  if (topNudgingStrategy === 'conditional-spread') {
    if (data.top_nudging_text) {
      basePrice.topNudging = data.top_nudging_text;
      basePrice.priceDescription = false;
    } else if (data.price_description) {
      basePrice.topNudging = false;
      basePrice.priceDescription = { name: data.price_description.text };
    }
  }

  const name = {
    goodsName: data.name,
    marketName: data.store_text ?? null,
    nameLineCount: data.name_lines,
    showBrand: false,
    ...nameOverrides,
  };

  const contents: Record<string, unknown> = {
    price: basePrice,
    name,
    imageAd: data.has_ad_badge,
    nudge: data.nudging_text ?? undefined,
    chips,
    ...contentsOverrides,
  };

  if (topNudgingStrategy === 'separate-fields') {
    contents.topNudging = data.top_nudging_text ?? undefined;
    contents.priceDescription = data.price_description
      ? { name: data.price_description.text }
      : undefined;
  }

  return {
    sno: item.sno,
    deliveryType: item.delivery_type,
    thumbnail: {
      src: thumbnailSrc,
      freesSrc: thumbnailSrc,
      closedReason: data.closed_reason ?? undefined,
      badges,
      emblems: data.emblems?.map(emblem => ({ uri: emblem.image_url })),
      ...thumbnailOverrides,
    },
    contents,
  } as GoodsData;
};
